import {
	type FormEvent, type SubmitEvent as ReactSubmitEvent, type ChangeEvent, Fragment, memo, useState
} from "react";
import "Components/ConfigLayout/Record/SetupSettings/SetupSettings.less";
import ActionButtons from "Components/ConfigLayout/ActionButtons";
import { Link } from "react-router-dom";
import {
	Checkbox, Icon, IconWeight, Popover, type SelectOption, Select, SettingsLayout, Card,
	IconSize
} from "@clintonelec/react-storybook";
import {
	RecordMode, postRecordingTimeOptions, preRecordingTimeOptions, recordingOptions, storageOptions
} from "Data/Objects/RecordSettings";
import { useAppDispatch, useAppSelector } from "Data/Redux/Store";
import { selectRecordSettings, setRecordSettingsAction } from "Data/Redux/Slices/Settings/Record/Record";
import type { RecordSettingsState } from "Interfaces";
import { isEqual, merge } from "lodash";
import { produce } from "immer";

const recordingModeNote =
	"You must use a 16GB or larger SD card to use recording storage. Older SD cards may not work.";

const eventRecordingNote = (
	<Fragment>
		<span>Go to&nbsp;</span>
		<Link to="/setup/system/schedule">Schedule Preset</Link>
		<span>&nbsp;to create a new preset.</span>
	</Fragment>
);

const recordSelectOptions: SelectOption[] = Object.values(RecordMode).map(option => ({
	label: option,
	value: option
}));

export type RecordSettingsFormFields = {
	recordingMode: HTMLInputElement;
	storageOption: HTMLInputElement;
	continuousRecordingOption: HTMLInputElement;
	eventRecordingOption: HTMLInputElement;
	recordingSchedule: RadioNodeList;
	preRecordingTime: HTMLInputElement;
	postRecordingTime: HTMLInputElement;
} & HTMLFormElement;

function RecordSetupSettings() {
	const dispatch = useAppDispatch();
	const recordSetupSettings = useAppSelector(selectRecordSettings);
	const [ recordSetupSettingsDiff, setRecordSetupSettingsDiff ] =
		useState<Partial<RecordSettingsState>>({});

	const localRecordSetupSettings = produce(recordSetupSettings, (draft) => {
		merge(draft, recordSetupSettingsDiff);
	});

	const setRecordSettings = () => {
		dispatch(setRecordSettingsAction(localRecordSetupSettings));
	};

	const handleReset = (event: FormEvent<RecordSettingsFormFields>) => {
		event.preventDefault();
		setRecordSetupSettingsDiff({});
	};

	const handleFormSubmit = (event: ReactSubmitEvent<RecordSettingsFormFields>) => {
		event.preventDefault();

		if (event.currentTarget?.checkValidity()) {
			setRecordSettings();
			setRecordSetupSettingsDiff({});
		}
	};

	const handleRecordingModeSelect = (newRecordingMode: string | null) => {
		if (!newRecordingMode) {
			return;
		}

		setRecordSetupSettingsDiff(produce(localRecordSetupSettings, draft => {
			draft.recordingMode = newRecordingMode as RecordMode;
		}));
	};

	const handleFormChange = (event: ChangeEvent<RecordSettingsFormFields>) => {
		const { recordingMode, recordingSchedule } = event.currentTarget;

		setRecordSetupSettingsDiff(produce(localRecordSetupSettings, (draft) => {
			draft.recordingMode = recordingMode?.value as RecordMode;

			Object.keys(localRecordSetupSettings.recordingSchedule).forEach(schedule => {
				recordingSchedule.forEach((input) => {
					if (!("id" in input) || !("checked" in input)) {
						return;
					}

					if (input.id === schedule) {
						draft.recordingSchedule[ schedule ] = input.checked as boolean;
					}
				});
			});
		}));
	};

	const handleStorageSelect = (value: string | null) => {
		if (!value) {
			return;
		}

		setRecordSetupSettingsDiff(produce(localRecordSetupSettings, draft => {
			draft.storageOption = value;
		}));
	};

	const handleContinuousRecordingSelect = (value: string | null) => {
		if (!value) {
			return;
		}

		setRecordSetupSettingsDiff(produce(localRecordSetupSettings, draft => {
			draft.continuousRecordingOption = value;
		}));
	};

	const handleEventRecordingSelect = (value: string | null) => {
		if (!value) {
			return;
		}

		setRecordSetupSettingsDiff(produce(localRecordSetupSettings, draft => {
			draft.eventRecordingOption = value;
		}));
	};

	const handlePreRecordingTimeSelect = (value: string | null) => {
		if (!value) {
			return;
		}

		setRecordSetupSettingsDiff(produce(localRecordSetupSettings, draft => {
			draft.preRecordingTime = +value;
		}));
	};

	const handlePostRecordingTimeSelect = (value: string | null) => {
		if (!value) {
			return;
		}

		setRecordSetupSettingsDiff(produce(localRecordSetupSettings, draft => {
			draft.postRecordingTime = +value;
		}));
	};

	const renderRecordingSchedule = () => {
		return Object.entries(localRecordSetupSettings.recordingSchedule).map((schedule, index) => {
			const [ scheduleName, checked ] = schedule;

			return (
				<Checkbox
					checked={ checked }
					disabled={ localRecordSetupSettings.recordingMode !== RecordMode.EVENT }
					id={ scheduleName }
					// eslint-disable-next-line react/no-array-index-key
					key={ `recordingSchedule-${ index }` }
					name="recordingSchedule"
				>
					<span>{ scheduleName }</span>
				</Checkbox>
			);
		});
	};

	return (
		<SettingsLayout.Form
			className="record-setup-settings"
			noValidate
			onReset={ handleReset }
			onSubmit={ handleFormSubmit }
			onChange={ handleFormChange }
		>
			<SettingsLayout.ScrollableContainer>
				<Card size="small" title="Recording Mode" popoverContent={ recordingModeNote }>
					<div className="record-setup-radio">
						<Select
							allowClear={ false }
							defaultValue={ localRecordSetupSettings.recordingMode }
							onSelect={ handleRecordingModeSelect }
							options={ recordSelectOptions }
							value={ localRecordSetupSettings.recordingMode }
						/>
					</div>
				</Card>
				<Card size="small" title="Storage Options">
					<div className="form-row">
						<span>Recording Storage</span>
						<Select
							allowClear={ false }
							className="form-select"
							disabled
							name="storageOption"
							onSelect={ handleStorageSelect }
							options={ storageOptions }
							value={ localRecordSetupSettings.storageOption }
						/>
					</div>
				</Card>
				<Card size="small" title="Continuous Recording Options">
					<div className="form-row">
						<span>Video Stream</span>
						<Select
							allowClear={ false }
							className="form-select"
							disabled={
								localRecordSetupSettings.recordingMode !== RecordMode.CONTINUOUS
							}
							name="continuousRecordingOption"
							onSelect={ handleContinuousRecordingSelect }
							options={ recordingOptions }
							value={ localRecordSetupSettings.continuousRecordingOption }
						/>
					</div>
				</Card>
				<Card size="small" title="Event Recording Options">
					<div className="form-row">
						<span>Video Stream</span>
						<Select
							allowClear={ false }
							className="form-select"
							disabled={
								localRecordSetupSettings.recordingMode !== RecordMode.EVENT
							}
							name="eventRecordingOption"
							onSelect={ handleEventRecordingSelect }
							options={ recordingOptions }
							value={ localRecordSetupSettings.eventRecordingOption }
						/>
					</div>
					<div className="form-row">
						<div className="recording-schedule-wrap">
							<span>Recording Schedule</span>
							<Popover content={ eventRecordingNote } wrapClassName="information-popover">
								<Icon iconWeight={ IconWeight.LIGHT } size={ IconSize.SMALLEST } name="circle-info" />
							</Popover>
						</div>
						<div className="checkbox-spacer" />
						<div className="schedules">
							{ renderRecordingSchedule() }
						</div>
					</div>
				</Card>
				<Card size="small" title="Recording Time">
					<div className="form-row">
						<span>Pre Recording Time</span>
						<Select
							allowClear={ false }
							className="form-select"
							name="preRecordingTime"
							onSelect={ handlePreRecordingTimeSelect }
							options={ preRecordingTimeOptions }
							value={ localRecordSetupSettings.preRecordingTime }
						/>
					</div>
					<div className="form-row">
						<span>Post Recording Time</span>
						<Select
							allowClear={ false }
							className="form-select"
							name="postRecordingTime"
							menuPlacement="auto"
							onSelect={ handlePostRecordingTimeSelect }
							options={ postRecordingTimeOptions }
							value={ localRecordSetupSettings.postRecordingTime }
						/>
					</div>
				</Card>
			</SettingsLayout.ScrollableContainer>
			<ActionButtons disabled={ isEqual(localRecordSetupSettings, recordSetupSettings) } />
		</SettingsLayout.Form>
	);
}

export default memo(RecordSetupSettings);
